import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import {
  handleEventKitConnect,
  handleOpenEventKitSettings,
} from '@/integrations/integration-handlers';
import { useEventKitState } from '@/integrations/useEventKitState';

import {
  accountChoice,
  choiceFor,
  displayNames,
  groupByAccount,
  summary,
  useAppleCalendarAccess,
} from './apple-calendar-access';
import { AccountHeader, CalendarRow, ChoiceMenu } from './apple-calendar-rows';
import { DevicePermissionBanner } from './device-permission-banner';

import type { ConfigurationContext } from './configuration.types';
import type { AppleCalendar } from '@littlebird/apple-calendar/contract';

type Access = ReturnType<typeof useAppleCalendarAccess>;

const LIST_FRAME =
  'rounded-lg border border-border bg-surface dark:border-border-strong';

function Message({ children }: { children: React.ReactNode }) {
  return (
    <YStack
      role="alert"
      className={`${LIST_FRAME} items-center gap-3 px-4 py-6 text-center`}
    >
      {children}
    </YStack>
  );
}

function AccountList({
  access,
  rows,
}: {
  access: Access;
  rows: AppleCalendar[];
}) {
  const { hidden, meetingsOff, ready, setChoice } = access;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const names = displayNames(rows);
  const counts = summary(rows, hidden, meetingsOff);

  return (
    <YStack className="gap-2">
      <XStack className="items-baseline gap-1.5">
        <span className="text-xs font-medium text-foreground-muted">
          Calendars
        </span>
        <span className="text-xs text-foreground-subtle">
          {`${counts.visible} of ${counts.total} visible · ${counts.meetings} used for meetings`}
        </span>
      </XStack>
      <YStack className={`${LIST_FRAME} overflow-hidden`}>
        {groupByAccount(rows).map((account) => {
          const accountHidden = hidden.accounts.has(account.id);
          const open = expanded[account.id] ?? false;
          return (
            <YStack
              key={account.id}
              className="border-t border-border first:border-t-0 dark:border-border-strong"
            >
              <AccountHeader
                name={account.name}
                expanded={open}
                muted={accountHidden}
                onToggle={() =>
                  setExpanded({ ...expanded, [account.id]: !open })
                }
                menu={
                  <ChoiceMenu
                    name={account.name}
                    value={accountChoice(
                      account.calendars,
                      hidden,
                      meetingsOff
                    )}
                    viewOnly={account.calendars.every((c) => !c.editable)}
                    disabled={!ready}
                    onChoose={(choice) =>
                      setChoice(
                        {
                          kind: 'account',
                          accountId: account.id,
                          calendars: account.calendars,
                        },
                        choice
                      )
                    }
                  />
                }
              />
              {open &&
                account.calendars.map((calendar) => {
                  const name = names.get(calendar.id) ?? calendar.name;
                  return (
                    <CalendarRow
                      key={calendar.id}
                      name={name}
                      color={calendar.color}
                      menu={
                        <ChoiceMenu
                          name={name}
                          value={choiceFor(calendar, hidden, meetingsOff)}
                          viewOnly={!calendar.editable}
                          disabled={!ready || accountHidden}
                          disabledReason={
                            accountHidden
                              ? 'Turn on the account first'
                              : undefined
                          }
                          onChoose={(choice) =>
                            setChoice({ kind: 'calendar', calendar }, choice)
                          }
                        />
                      }
                    />
                  );
                })}
            </YStack>
          );
        })}
      </YStack>
    </YStack>
  );
}

function CalendarContent({ access }: { access: Access }) {
  const { calendarsQuery } = access;
  const result = calendarsQuery.data;
  if (calendarsQuery.isPending) {
    return (
      <XStack center role="status" className={`${LIST_FRAME} gap-2 px-4 py-4`}>
        <Icons.loader className="size-3.5 animate-spin text-foreground-muted" />
        <span className="text-xs text-foreground-muted">
          Loading calendars…
        </span>
      </XStack>
    );
  }
  if (result?.ok)
    return <AccountList access={access} rows={result.calendars} />;
  if (result?.problem === 'access_denied') return null;
  return (
    <Message>
      <p className="text-xs text-foreground-muted">
        Couldn&apos;t load calendars from this Mac.
      </p>
      <Button
        variant="secondary"
        size="sm"
        isLoading={calendarsQuery.isFetching}
        onClick={() => void calendarsQuery.refetch()}
      >
        Try again
      </Button>
    </Message>
  );
}

export function AppleCalendarConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const serverSlug =
    context.localSource?.kind === 'local_mcp'
      ? context.localSource.serverSlug
      : undefined;
  const access = useAppleCalendarAccess(serverSlug);
  const { calendarStatus } = useEventKitState();
  const denied =
    access.calendarsQuery.data?.ok === false &&
    access.calendarsQuery.data.problem === 'access_denied';
  const status =
    denied && calendarStatus === 'authorized' ? 'denied' : calendarStatus;

  return (
    <YStack className="gap-4 p-6">
      <DevicePermissionBanner
        status={status}
        domain="Calendar"
        domainPlural="calendars"
        onGrantAccess={() => handleEventKitConnect('applecalendar')}
        onOpenSettings={() => handleOpenEventKitSettings('applecalendar')}
      />
      <CalendarContent access={access} />
    </YStack>
  );
}
